import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { mockGoodsReceiptService } from '@/services/mock/MockGoodsReceiptService';
import { GoodsReceiptRecord, GrnFilterState, GrnMetricsSummary } from '@/types/domain/grn-management';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { formatDate } from '@/lib/utils';
import {
  PackageCheck,
  RefreshCw,
  Search,
  Eye,
  Pencil,
  Trash2,
  Database,
  AlertTriangle,
  Plus,
  Download,
  ShieldCheck,
  Truck,
  Loader2,
  X,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { GrnKpiCards } from '@/features/grn/components/GrnKpiCards';
import { usePermission } from '@/hooks/usePermission';
import { SlideOverDrawer } from '@/components/ui/SlideOverDrawer';
import { GrnDetailPage } from './GrnDetailPage';

const renderGrnStatus = (status: GoodsReceiptRecord['status']) => {
  let color = 'bg-primary/10 text-primary border-primary/20';
  let dot = 'bg-primary';

  switch (status) {
    case 'ACCEPTED':
      color = 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20';
      dot = 'bg-emerald-500';
      break;
    case 'PARTIALLY_ACCEPTED':
      color = 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20';
      dot = 'bg-blue-500';
      break;
    case 'QUALITY_HOLD':
      color = 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20';
      dot = 'bg-purple-500';
      break;
    case 'REJECTED':
    case 'CANCELLED':
      color = 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20';
      dot = 'bg-rose-500';
      break;
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-2xs font-poppins whitespace-nowrap w-36 justify-center ${color}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dot} shrink-0`} />
      <span className="truncate">{status.replace(/_/g, ' ')}</span>
    </span>
  );
};

export const GrnListPage: React.FC = () => {
  const navigate = useNavigate();
  const { can } = usePermission();
  const [metrics, setMetrics] = useState<GrnMetricsSummary | null>(null);
  const [records, setRecords] = useState<GoodsReceiptRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Deletion/Cancellation Modal State
  const [grnToDelete, setGrnToDelete] = useState<GoodsReceiptRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedGrnForDrawer, setSelectedGrnForDrawer] = useState<string | null>(null);

  const [filters, setFilters] = useState<GrnFilterState>({
    search: '',
    status: 'ALL',
    qualityStatus: 'ALL',
    plant: 'ALL',
    vendor: 'ALL',
    sortBy: 'receivedDate',
    sortOrder: 'desc',
    page: 1,
    pageSize: 25,
  });

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [m, grnRes] = await Promise.all([
        mockGoodsReceiptService.getGrnMetricsSummary(),
        mockGoodsReceiptService.getGoodsReceipts(filters),
      ]);
      setMetrics(m);
      setRecords(grnRes.items);
      setTotal(grnRes.total);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (!grnToDelete) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = orig;
    };
  }, [grnToDelete]);

  const handleExportCsv = () => {
    if (records.length === 0) return;
    const headers = [
      'GRN Number',
      'SAP MIGO Doc #',
      'PO Number',
      'Supplier',
      'Plant',
      'Storage Location',
      'Received Date',
      'Total Received',
      'Total Accepted',
      'Total Rejected',
      'Status',
      'Quality Status',
    ];
    const rows = records.map((r) => [
      r.grnNumber,
      r.sapGrnNumber || 'N/A',
      r.poNumber,
      `"${r.vendorName}"`,
      r.plantCode,
      r.storageLocation,
      r.receivedDate,
      r.totalReceivedQuantity,
      r.totalAcceptedQuantity,
      r.totalRejectedQuantity,
      r.status,
      r.qualityStatus,
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Goods_Receipts_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleConfirmCancel = async () => {
    if (!grnToDelete) return;
    try {
      setIsDeleting(true);
      await mockGoodsReceiptService.cancelGrn(grnToDelete.id, 'Administrative cancellation requested by warehouse manager.');
      setGrnToDelete(null);
      loadData();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to reverse goods receipt note.');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: ColumnDef<GoodsReceiptRecord>[] = [
    {
      header: 'GRN Number',
      accessorKey: 'grnNumber',
      sortable: true,
      cell: (row) => (
        <div className="space-y-0.5 font-poppins">
          <span
            onClick={() => navigate(ROUTES.GRN.DETAILS(row.id))}
            className="font-poppins font-bold text-primary hover:underline cursor-pointer whitespace-nowrap text-sm block"
          >
            {row.grnNumber}
          </span>
          <div className="flex items-center gap-1.5 text-xs text-foreground-muted font-mono whitespace-nowrap">
            {row.sapGrnNumber ? (
              <span className="text-purple-600 dark:text-purple-400 font-bold flex items-center gap-0.5">
                <Database className="h-3 w-3" /> MIGO: {row.sapGrnNumber}
              </span>
            ) : (
              <span>MIGO: Pending 101</span>
            )}
            <span>•</span>
            <span>Rev {row.version}</span>
          </div>
        </div>
      ),
    },
    {
      header: 'PO Reference & Supplier',
      accessorKey: 'poNumber',
      sortable: true,
      cell: (row) => (
        <div className="space-y-0.5 font-poppins min-w-0 max-w-[190px] xl:max-w-[220px]">
          <span
            onClick={(e) => {
              e.stopPropagation();
              navigate(ROUTES.PURCHASE_ORDERS.DETAILS(row.poId));
            }}
            className="font-mono font-bold text-primary hover:underline cursor-pointer block text-xs"
          >
            {row.poNumber}
          </span>
          <span className="text-xs font-bold text-foreground truncate block" title={row.vendorName}>
            {row.vendorName}
          </span>
        </div>
      ),
    },
    {
      header: 'Plant & Storage Location',
      accessorKey: 'plantCode',
      sortable: true,
      cell: (row) => (
        <div className="space-y-0.5 font-poppins text-xs">
          <span className="font-semibold text-foreground block">
            {row.plantCode}
          </span>
          <span className="text-[10px] text-foreground-muted block whitespace-nowrap">
            {row.storageLocation} — {row.unloadingDock}
          </span>
        </div>
      ),
    },
    {
      header: 'Quantities (Recv / Acc / Rej)',
      cell: (row) => (
        <div className="font-poppins text-xs font-mono space-y-0.5">
          <div className="flex items-center gap-1.5 font-bold">
            <span className="text-foreground">{row.totalReceivedQuantity} Recv</span>
            <span className="text-foreground-muted">•</span>
            <span className="text-emerald-600 dark:text-emerald-400">{row.totalAcceptedQuantity} Acc</span>
            {row.totalRejectedQuantity > 0 && (
              <>
                <span className="text-foreground-muted">•</span>
                <span className="text-rose-600 dark:text-rose-400 font-bold">{row.totalRejectedQuantity} Rej</span>
              </>
            )}
          </div>
          <span className="text-[10px] text-foreground-muted font-sans font-medium block">
            Rec'd: {formatDate(row.receivedDate)} • DC #{row.deliveryChallanNumber}
          </span>
        </div>
      ),
    },
    {
      header: 'Quality Inspection',
      accessorKey: 'qualityStatus',
      cell: (row) => (
        <div className="space-y-0.5 font-poppins text-xs">
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-extrabold px-2.5 py-0.5 rounded-full ${
              row.qualityStatus === 'PASSED'
                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                : row.qualityStatus === 'FAILED'
                ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20'
                : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20'
            }`}
          >
            <ShieldCheck className="h-3 w-3" /> {row.qualityStatus}
          </span>
          <span className="text-[10px] text-foreground-muted block whitespace-nowrap">
            By {row.inspectorName}
          </span>
        </div>
      ),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: (row) => (
        <div className="flex items-center font-poppins">
          {renderGrnStatus(row.status)}
        </div>
      ),
    },
    {
      header: 'Actions',
      cell: (row) => (
        <div
          className="flex items-center gap-1.5 justify-start shrink-0 whitespace-nowrap font-poppins min-w-[110px]"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedGrnForDrawer(row.id)}
            title="View Goods Receipt Dossier (Slide-over)"
            className="h-8 w-8 p-0 rounded-lg hover:bg-primary/15 hover:text-primary active:scale-90 transition-all duration-200 cursor-pointer text-foreground-secondary shrink-0 inline-flex items-center justify-center group shadow-2xs hover:shadow-xs"
          >
            <Eye className="h-4 w-4 transition-transform duration-200 group-hover:scale-115" />
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(ROUTES.GRN.EDIT(row.id))}
            title="Edit Receiving Details"
            className="h-8 w-8 p-0 rounded-lg hover:bg-primary/10 cursor-pointer text-primary hover:text-primary-dark shrink-0 inline-flex items-center justify-center"
          >
            <Pencil className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setGrnToDelete(row)}
            title="Reverse / Cancel GRN (102 Movement)"
            className="h-8 w-8 p-0 rounded-lg text-rose-500 hover:text-rose-600 hover:bg-rose-500/15 cursor-pointer shrink-0 inline-flex items-center justify-center"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 pb-12 font-poppins">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-2.5 font-poppins">
            <PackageCheck className="h-7 w-7 text-primary" /> Goods Receipt Notes (GRN / MIGO) Control Center
          </h1>
          <p className="text-xs text-foreground-secondary font-medium mt-1 font-poppins">
            Warehouse inwarding, quality inspection reconciliation, and simulated SAP S/4HANA MIGO Movement 101 postings.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            className="rounded-xl font-bold font-poppins cursor-pointer"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.DELIVERIES.TRACKING)}
            className="rounded-xl font-bold font-poppins cursor-pointer border-border"
          >
            <Truck className="h-4 w-4 mr-1.5" /> Delivery Tracking
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            className="rounded-xl font-bold font-poppins cursor-pointer border-border"
          >
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>

          {can('GRN_CREATE') && (
            <Button
              onClick={() => navigate(ROUTES.GRN.NEW)}
              className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground font-poppins cursor-pointer shadow-xs"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Receive Goods (Create GRN)
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards Strip */}
      {metrics && (
        <GrnKpiCards
          metrics={metrics}
          onSelectFilter={(type, val) => {
            if (type === 'qualityStatus') {
              setFilters((prev) => ({ ...prev, qualityStatus: val }));
            }
          }}
        />
      )}

      {/* Main Card with Squeezed Status Tabs & Search */}
      <Card className="border-border rounded-2xl shadow-xs overflow-hidden font-poppins">
        <CardHeader className="p-4 border-b border-border/60 bg-muted/20">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            {/* Squeezed Status Tabs */}
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { key: 'ALL', label: 'All Receipts' },
                { key: 'ACCEPTED', label: 'Fully Accepted' },
                { key: 'PARTIALLY_ACCEPTED', label: 'Partially Accepted' },
                { key: 'QUALITY_HOLD', label: 'Quality Hold' },
                { key: 'REJECTED', label: 'Rejected' },
                { key: 'CANCELLED', label: 'Cancelled' },
              ].map((tab) => {
                const isActive = filters.status === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setFilters((prev) => ({ ...prev, status: tab.key }))}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer font-poppins ${
                      isActive
                        ? 'bg-primary text-primary-foreground font-bold shadow-xs'
                        : 'bg-card border border-border text-foreground-secondary hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Search Input */}
            <div className="relative min-w-[240px]">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                placeholder="Search GRN #, PO #, SAP #, supplier..."
                className="w-full h-9 pl-9 pr-3 text-xs font-medium rounded-xl border border-input bg-background text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary font-poppins"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-4 overflow-x-auto">
          <DataTable
            data={records}
            columns={columns}
            isLoading={isLoading}
            pageSize={25}
            totalCount={total}
            searchPlaceholder="Search goods receipts..."
            fixedHeight={true}
            containerHeightClass="max-h-[580px] overflow-y-auto overflow-x-auto custom-scrollbar"
            emptyTitle="No Goods Receipts Found"
            emptyDescription="There are no goods receipt notes matching your current filter criteria."
            onRowClick={(row) => navigate(ROUTES.GRN.DETAILS(row.id))}
          />
        </CardContent>
      </Card>

      {/* Cancel / Reversal Confirmation Modal (Mounted via createPortal to document.body) */}
      {grnToDelete &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-in fade-in"
            onClick={() => !isDeleting && setGrnToDelete(null)}
          >
            <div
              className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl space-y-4 font-poppins animate-in zoom-in-95 my-auto text-foreground"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-border/60 pb-3">
                <div className="flex items-center gap-2 text-rose-600">
                  <AlertTriangle className="h-5 w-5" />
                  <h3 className="text-base font-bold text-foreground font-poppins">
                    Reverse & Cancel Goods Receipt (102)?
                  </h3>
                </div>
                <button
                  onClick={() => !isDeleting && setGrnToDelete(null)}
                  className="rounded-lg p-1 hover:bg-muted text-foreground-muted cursor-pointer"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* GRN Details Box */}
              <div className="rounded-xl bg-muted/40 border border-border p-3.5 space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    GRN Number
                  </span>
                  <span className="font-mono font-bold text-primary">
                    {grnToDelete.grnNumber}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    PO Reference
                  </span>
                  <span className="font-mono font-bold text-foreground">
                    {grnToDelete.poNumber}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Supplier
                  </span>
                  <span className="font-bold text-foreground truncate max-w-[200px] text-right">
                    {grnToDelete.vendorName}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Accepted Units
                  </span>
                  <span className="font-mono font-bold text-emerald-600">
                    {grnToDelete.totalAcceptedQuantity} units
                  </span>
                </div>
              </div>

              <p className="text-xs text-foreground-secondary leading-relaxed">
                Cancelling this GRN initiates an SAP MIGO Movement 102 reversal. Inwarded inventory will be deducted from warehouse storage locations and PO line quantities will be reopened for re-delivery.
              </p>

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-border/60">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isDeleting}
                  onClick={() => setGrnToDelete(null)}
                  className="cursor-pointer font-bold rounded-xl font-poppins"
                >
                  Keep GRN
                </Button>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={handleConfirmCancel}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 active:scale-95 text-white font-extrabold text-xs shadow-lg shadow-rose-600/30 transition-all cursor-pointer disabled:opacity-50 font-poppins"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>Reversing...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-3.5 w-3.5" />
                      <span>Confirm Reversal (102)</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Right Slide-Over Curtain Drawer for GRN Dossier */}
      <SlideOverDrawer
        isOpen={Boolean(selectedGrnForDrawer)}
        onClose={() => setSelectedGrnForDrawer(null)}
        title="Goods Receipt Dossier"
        subtitle="Warehouse gate inwarding, physical count, quality inspection, and SAP MIGO 101/102 movements"
        fullPageUrl={selectedGrnForDrawer ? ROUTES.GRN.DETAILS(selectedGrnForDrawer) : undefined}
      >
        {selectedGrnForDrawer && (
          <GrnDetailPage
            recordId={selectedGrnForDrawer}
            isDrawer={true}
            onClose={() => setSelectedGrnForDrawer(null)}
          />
        )}
      </SlideOverDrawer>
    </div>
  );
};
